import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Users,
  Search,
  Plus,
  Download,
  Upload,
  X,
  Loader2,
  Mail,
  Building2,
  Tag as TagIcon,
  RefreshCw,
  FileText,
  CheckCircle2,
  AlertTriangle,
  ListPlus,
  Send,
  FolderPlus,
  Inbox as InboxIcon,
} from "lucide-react";
import Papa from "papaparse";
import { toast } from "sonner";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  // RFC4180: wrap in quotes if it contains comma, quote, or newline.
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(rows) {
  const header = ["First Name", "Last Name", "Email", "Company", "Tags", "Created"];
  const body = rows.map((r) =>
    [
      r.first_name,
      r.last_name,
      r.email,
      r.company,
      (r.tags || []).join("; "),
      r.created_at,
    ]
      .map(csvEscape)
      .join(","),
  );
  return [header.join(","), ...body].join("\r\n");
}

function downloadCsv(filename, contents) {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseTagsInput(raw) {
  return (raw || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function formatErr(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => e?.msg || (typeof e === "string" ? e : JSON.stringify(e)))
      .join(" · ");
  }
  return fallback;
}

function initials(first, last, email) {
  const a = (first || "").trim()[0] || "";
  const b = (last || "").trim()[0] || "";
  const fallback = (email || "").trim()[0] || "?";
  return ((a + b) || fallback).toUpperCase();
}

// ---------------------------------------------------------------------------
// Slide-over: New Lead
// ---------------------------------------------------------------------------

function NewLeadDrawer({ open, onClose, onCreated }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    } else {
      setFirstName("");
      setLastName("");
      setEmail("");
      setCompany("");
      setTagsRaw("");
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required.");
      return;
    }
    setBusy(true);
    try {
      const tags = parseTagsInput(tagsRaw);
      const r = await api.post("/leads", {
        first_name: firstName,
        last_name: lastName,
        email,
        company,
        tags,
      });
      toast.success("Lead added");
      onCreated?.(r.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatErr(detail, err.message || "Could not add lead."));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="new-lead-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        data-testid="new-lead-overlay"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md h-full bg-[#0a0a0a] border-l border-[#1a1a1a] shadow-2xl overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a1a]">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              CRM
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-1">
              New lead
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="new-lead-close"
            className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors"
            aria-label="Close panel"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 flex flex-col">
          <div className="px-6 py-5 space-y-4 flex-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                  First name
                </label>
                <input
                  ref={firstFieldRef}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="lead-first-name-input"
                  className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#2a2a2a]"
                  placeholder="Ada"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                  Last name
                </label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  data-testid="lead-last-name-input"
                  className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#2a2a2a]"
                  placeholder="Lovelace"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="lead-email-input"
                required
                className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#2a2a2a]"
                placeholder="ada@analytical.io"
              />
            </div>

            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                Company
              </label>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                data-testid="lead-company-input"
                className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#2a2a2a]"
                placeholder="Analytical Engine"
              />
            </div>

            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                Tags
              </label>
              <input
                value={tagsRaw}
                onChange={(e) => setTagsRaw(e.target.value)}
                data-testid="lead-tags-input"
                className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#2a2a2a] font-mono"
                placeholder="vip, founder, demo-q3"
              />
              <p className="text-[11px] text-neutral-600 mt-1.5">
                Comma-separated. Stored as an array.
              </p>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-end gap-2 sticky bottom-0 bg-[#0a0a0a]">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="lead-submit-button"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-4 py-2 rounded-md font-medium text-sm disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
              ) : (
                <Plus className="w-4 h-4" strokeWidth={1.8} />
              )}
              Add lead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV Import: parser + mapping modal
// ---------------------------------------------------------------------------

const STANDARD_FIELDS = [
  { value: "__ignore", label: "— Ignore column —" },
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "email", label: "Email" },
  { value: "company", label: "Company" },
  { value: "tags", label: "Tags" },
  { value: "__custom", label: "Custom field…" },
];

function autoGuessMapping(headers) {
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");
  const rules = [
    { match: ["firstname", "fname", "givenname"], to: "first_name" },
    { match: ["lastname", "lname", "surname", "familyname"], to: "last_name" },
    { match: ["fullname", "name"], to: "first_name" },
    { match: ["email", "emailaddress", "mail"], to: "email" },
    { match: ["company", "organization", "organisation", "employer", "account"], to: "company" },
    { match: ["tag", "tags", "labels", "label"], to: "tags" },
  ];
  return headers.map((h) => {
    const k = norm(h);
    for (const r of rules) {
      if (r.match.includes(k)) {
        return { header: h, field: r.to, customKey: "" };
      }
    }
    return { header: h, field: "__ignore", customKey: "" };
  });
}

function transformRows(rows, mappings) {
  const lookup = Object.fromEntries(mappings.map((m) => [m.header, m]));
  const out = [];
  const skipped = [];
  rows.forEach((row, idx) => {
    const lead = {
      first_name: "",
      last_name: "",
      email: "",
      company: "",
      tags: [],
      custom_fields: {},
    };
    Object.entries(row).forEach(([header, rawValue]) => {
      const m = lookup[header];
      if (!m || m.field === "__ignore") return;
      const value =
        rawValue === null || rawValue === undefined ? "" : String(rawValue).trim();
      if (!value) return;
      if (m.field === "tags") {
        lead.tags = value;
      } else if (m.field === "__custom") {
        const key = (m.customKey || header).trim();
        if (key) lead.custom_fields[key] = value;
      } else if (
        ["first_name", "last_name", "email", "company"].includes(m.field)
      ) {
        lead[m.field] = value;
      }
    });
    if (!lead.email) {
      skipped.push({ rowIndex: idx + 1, reason: "missing email" });
      return;
    }
    out.push(lead);
  });
  return { rows: out, skipped };
}

function ImportCsvModal({ open, onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setHeaders([]);
      setRows([]);
      setMappings([]);
      setParsing(false);
      setUploading(false);
      setDragOver(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !uploading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, uploading]);

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name) && f.type !== "text/csv") {
      toast.error("Please upload a .csv file.");
      return;
    }
    setFile(f);
    setParsing(true);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.trim(),
      complete: (results) => {
        setParsing(false);
        const fields = (results.meta && results.meta.fields) || [];
        if (fields.length === 0) {
          toast.error("Couldn't detect any columns in that CSV.");
          setFile(null);
          return;
        }
        setHeaders(fields);
        setRows(results.data || []);
        setMappings(autoGuessMapping(fields));
        if (results.errors && results.errors.length) {
          toast.warning(
            `Parsed with ${results.errors.length} CSV warning(s) — review carefully.`,
          );
        }
      },
      error: (err) => {
        setParsing(false);
        setFile(null);
        toast.error(`CSV parse failed: ${err?.message || "unknown error"}`);
      },
    });
  };

  const updateMapping = (idx, patch) => {
    setMappings((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const emailMapped = mappings.some((m) => m.field === "email");
  const customsValid = mappings.every(
    (m) => m.field !== "__custom" || (m.customKey && m.customKey.trim()),
  );
  const canSubmit =
    !parsing && !uploading && headers.length > 0 && emailMapped && customsValid;

  const submit = async () => {
    if (!canSubmit) return;
    const { rows: payloadRows, skipped } = transformRows(rows, mappings);
    if (payloadRows.length === 0) {
      toast.error("No valid rows to import (every row was missing an email).");
      return;
    }
    setUploading(true);
    try {
      const r = await api.post("/leads/bulk", { leads: payloadRows });
      const inserted = r.data?.inserted ?? r.data?.leads?.length ?? 0;
      const msg =
        skipped.length > 0
          ? `Imported ${inserted} · skipped ${skipped.length} (no email)`
          : `Imported ${inserted} leads`;
      toast.success(msg);
      onImported?.(r.data?.leads || []);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatErr(detail, err.message || "Bulk import failed."));
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="import-csv-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        data-testid="import-csv-overlay"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={uploading ? undefined : onClose}
      />
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a1a]">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              CRM &middot; Bulk import
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-1">
              Import leads from CSV
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            data-testid="import-csv-close"
            className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors disabled:opacity-30"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {headers.length === 0 && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="import-csv-dropzone"
                className={`w-full border border-dashed rounded-lg p-10 flex flex-col items-center justify-center text-center transition-colors ${
                  dragOver
                    ? "border-white/30 bg-white/5"
                    : "border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d]"
                }`}
              >
                <Upload className="w-7 h-7 text-neutral-500" strokeWidth={1.5} />
                <div className="mt-4 text-sm text-white">
                  {parsing ? "Reading CSV…" : "Drop a CSV file here, or click to browse"}
                </div>
                <div className="mt-1.5 text-xs text-neutral-500 max-w-md">
                  We use PapaParse client-side to detect headers. Nothing is
                  uploaded until you confirm the column mapping.
                </div>
                {parsing && (
                  <Loader2 className="w-4 h-4 animate-spin mt-3 text-neutral-400" strokeWidth={1.8} />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="import-csv-file-input"
                className="hidden"
              />
            </>
          )}

          {headers.length > 0 && (
            <>
              <div className="flex items-center gap-3 text-xs text-neutral-400">
                <FileText className="w-3.5 h-3.5" strokeWidth={1.8} />
                <span className="font-mono truncate" data-testid="import-csv-filename">
                  {file?.name}
                </span>
                <span className="text-neutral-700">·</span>
                <span className="font-mono">
                  {rows.length} row{rows.length === 1 ? "" : "s"}
                </span>
                <span className="text-neutral-700">·</span>
                <span className="font-mono">
                  {headers.length} column{headers.length === 1 ? "" : "s"}
                </span>
              </div>

              {!emailMapped && (
                <div className="mt-4 flex items-start gap-3 rounded-md border border-amber-400/30 bg-amber-400/5 p-3 text-sm text-amber-200">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
                  <span>
                    Map one column to <span className="font-mono">Email</span> to
                    enable import. Rows without an email are skipped.
                  </span>
                </div>
              )}

              <div
                className="mt-5 rounded-lg border border-[#1a1a1a] overflow-hidden"
                data-testid="import-csv-mapping-table"
              >
                <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-[#0d0d0d] text-[10.5px] font-mono uppercase tracking-[0.2em] text-neutral-500 border-b border-[#1a1a1a]">
                  <div className="col-span-4">CSV column</div>
                  <div className="col-span-3">Sample</div>
                  <div className="col-span-3">Maps to</div>
                  <div className="col-span-2">Custom key</div>
                </div>
                {mappings.map((m, idx) => {
                  const sample =
                    rows
                      .map((r) => r[m.header])
                      .find((v) => v !== undefined && v !== null && String(v).trim()) || "";
                  return (
                    <div
                      key={m.header}
                      data-testid={`import-csv-mapping-row-${idx}`}
                      className="grid grid-cols-12 gap-3 px-4 py-2.5 items-center border-t border-[#161616] text-sm"
                    >
                      <div className="col-span-4 font-mono text-neutral-200 truncate" title={m.header}>
                        {m.header}
                      </div>
                      <div className="col-span-3 text-neutral-500 text-xs truncate" title={String(sample)}>
                        {String(sample) || <span className="text-neutral-700">—</span>}
                      </div>
                      <div className="col-span-3">
                        <select
                          value={m.field}
                          onChange={(e) => updateMapping(idx, { field: e.target.value })}
                          data-testid={`import-csv-field-select-${idx}`}
                          className="w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-2.5 py-2 text-sm text-white outline-none font-mono"
                        >
                          {STANDARD_FIELDS.map((f) => (
                            <option key={f.value} value={f.value}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        {m.field === "__custom" ? (
                          <input
                            value={m.customKey}
                            onChange={(e) => updateMapping(idx, { customKey: e.target.value })}
                            placeholder="e.g. linkedin"
                            data-testid={`import-csv-custom-key-${idx}`}
                            className="w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-2.5 py-2 text-xs text-white outline-none font-mono"
                          />
                        ) : (
                          <span className="text-neutral-700 text-xs font-mono">—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 text-[11px] text-neutral-500 leading-relaxed">
                Standard fields land at the root of each lead. Anything mapped to
                <span className="text-neutral-300 mx-1">Custom field…</span>
                is nested inside the lead's
                <span className="font-mono text-neutral-300 mx-1">custom_fields</span>
                dictionary using the key you specify.
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-between gap-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-600">
            {headers.length === 0
              ? "Step 1 of 2 · Choose file"
              : emailMapped
                ? "Step 2 of 2 · Confirm mapping"
                : "Step 2 of 2 · Email column required"}
          </div>
          <div className="flex items-center gap-2">
            {headers.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setHeaders([]);
                  setRows([]);
                  setMappings([]);
                }}
                disabled={uploading}
                className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-3 py-2 disabled:opacity-30"
              >
                Use different file
              </button>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="import-csv-submit"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-4 py-2 rounded-md font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
              ) : (
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
              )}
              Import {headers.length > 0 ? `${rows.length} row${rows.length === 1 ? "" : "s"}` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 8A: Add-to-List modal
// ---------------------------------------------------------------------------

function AddToListModal({ open, onClose, lists, onConfirm, onCreateList, count }) {
  const [selectedListId, setSelectedListId] = useState("");
  const [creating, setCreating] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedListId(lists?.[0]?.id || "");
      setCreating(false);
      setNewListName("");
      setBusy(false);
    }
  }, [open, lists]);

  const submit = async () => {
    if (creating) {
      const name = newListName.trim();
      if (!name) {
        toast.error("List name cannot be empty.");
        return;
      }
      setBusy(true);
      try {
        const created = await onCreateList(name);
        if (created?.id) {
          await onConfirm(created.id);
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!selectedListId) {
      toast.error("Pick a list or create a new one.");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(selectedListId);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="add-to-list-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={busy ? undefined : onClose}
      />
      <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a1a]">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              Bulk · {count} lead{count === 1 ? "" : "s"}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-1">
              Add to list
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors disabled:opacity-30"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {!creating && (lists?.length || 0) > 0 && (
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                Existing list
              </label>
              <select
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                data-testid="add-to-list-select"
                className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-3 py-2.5 text-sm text-white outline-none font-mono"
              >
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} · {l.lead_count || 0}
                  </option>
                ))}
              </select>
            </div>
          )}

          {creating ? (
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                New list name
              </label>
              <input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                data-testid="new-list-name-input"
                autoFocus
                className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-3 py-2.5 text-sm text-white outline-none"
                placeholder="EU founders · Q3"
              />
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="mt-2 text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
              >
                ← Pick an existing list
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="create-new-list-toggle"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-neutral-300 hover:text-white border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 rounded-md"
            >
              <FolderPlus className="w-3.5 h-3.5" strokeWidth={1.8} />
              Create new list
            </button>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-3 py-2 disabled:opacity-30"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || (!creating && !selectedListId && (lists?.length || 0) === 0)}
            data-testid="add-to-list-confirm"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <ListPlus className="w-4 h-4" strokeWidth={1.8} />
            )}
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 8A: Push-to-Campaign modal
// ---------------------------------------------------------------------------

function PushToCampaignModal({ open, onClose, onConfirm, count }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedId("");
    api
      .get("/campaigns")
      .then((r) => {
        const list = (r.data?.campaigns || r.data || []).filter((c) =>
          ["draft", "active", "paused"].includes(c.status),
        );
        setCampaigns(list);
        if (list.length > 0) setSelectedId(list[0].id);
      })
      .catch((err) => {
        toast.error(formatErr(err?.response?.data?.detail, "Could not load campaigns."));
      })
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    if (!selectedId) {
      toast.error("Pick a campaign.");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(selectedId);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="push-to-campaign-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={busy ? undefined : onClose}
      />
      <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a1a]">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              Bulk · {count} lead{count === 1 ? "" : "s"}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-1">
              Push to campaign
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors disabled:opacity-30"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-400">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
              <span className="font-mono text-xs uppercase tracking-[0.2em]">
                Loading campaigns…
              </span>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-sm text-neutral-400">
              You don't have any draft, active, or paused campaigns yet.
              Create one in <span className="text-white">Campaigns</span> first.
            </div>
          ) : (
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                Target campaign
              </label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                data-testid="push-campaign-select"
                className="mt-1.5 w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-3 py-2.5 text-sm text-white outline-none font-mono"
              >
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.status}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                Leads are cloned into the campaign as fresh contacts. If the
                campaign is <span className="text-neutral-300">active</span> they
                are queued immediately; <span className="text-neutral-300">draft</span> /
                <span className="text-neutral-300"> paused</span> hold them
                until you activate.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-3 py-2 disabled:opacity-30"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || loading || !selectedId}
            data-testid="push-campaign-confirm"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <Send className="w-4 h-4" strokeWidth={1.8} />
            )}
            Push
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 8B: Polished New-List modal (replaces the lazy window.prompt)
// ---------------------------------------------------------------------------

function NewListModal({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("List name cannot be empty.");
      return;
    }
    setBusy(true);
    try {
      const created = await onCreate(trimmed);
      if (created) onClose();
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="new-list-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={busy ? undefined : onClose}
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a1a]">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              CRM &middot; Lists
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight mt-1">
              New list
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="new-list-modal-close"
            className="p-2 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors disabled:opacity-30"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
            Name <span className="text-red-400">*</span>
          </label>
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="new-list-modal-input"
            maxLength={120}
            placeholder="EU founders · Q3"
            className="w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none"
          />
          <p className="text-[11px] text-neutral-600 leading-relaxed">
            Lists are private to your account. Add leads from the bulk action
            bar after creation.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-[#1a1a1a] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="new-list-modal-cancel"
            className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-3 py-2 disabled:opacity-30"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="new-list-modal-submit"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <FolderPlus className="w-4 h-4" strokeWidth={1.8} />
            )}
            Add list
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("");

  // Stage 8A — selection state + lists state + active filter.
  const [selectedLeads, setSelectedLeads] = useState([]); // array of lead.id
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(""); // "" → All Leads
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  // Stage 8B — polished new-list modal supersedes the window.prompt.
  const [newListModalOpen, setNewListModalOpen] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      const r = await api.get("/lists");
      setLists(r.data?.lists || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Lists endpoint missing or empty is non-fatal — sidebar just stays empty.
      if (err?.response?.status !== 404) {
        toast.error(formatErr(detail, err.message || "Could not load lists."));
      }
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeListId ? { list_id: activeListId } : {};
      const r = await api.get("/leads", { params });
      setLeads(r.data?.leads || []);
      // Drop any selection that no longer exists in the new view.
      const visibleIds = new Set((r.data?.leads || []).map((l) => l.id));
      setSelectedLeads((prev) => prev.filter((id) => visibleIds.has(id)));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatErr(detail, err.message || "Could not load leads."));
    } finally {
      setLoading(false);
    }
  }, [activeListId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const allTags = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => (l.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (activeTag && !(l.tags || []).includes(activeTag)) return false;
      if (!q) return true;
      const hay = [
        l.first_name,
        l.last_name,
        `${l.first_name || ""} ${l.last_name || ""}`,
        l.email,
        l.company,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [leads, query, activeTag]);

  // Selection helpers — operate on `filtered` so master toggle reflects the
  // currently-visible view, never the unfiltered superset.
  const filteredIds = useMemo(() => filtered.map((l) => l.id), [filtered]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedLeads.includes(id));
  const someFilteredSelected =
    filteredIds.some((id) => selectedLeads.includes(id)) && !allFilteredSelected;

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedLeads((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedLeads((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const clearSelection = () => setSelectedLeads([]);

  const handleCreated = (lead) => {
    setLeads((prev) => [lead, ...prev]);
  };

  const handleBulkImported = (importedLeads) => {
    load();
    if (importedLeads && importedLeads.length === 0) {
      toast.message("Import complete — no new rows returned.");
    }
  };

  // Page-level export = current filtered view.
  const handleExport = () => {
    if (filtered.length === 0) {
      toast.error("Nothing to export — try clearing the filters.");
      return;
    }
    const csv = buildCsv(filtered);
    const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    downloadCsv(`outreachos-leads-${ts}.csv`, csv);
    toast.success(`Exported ${filtered.length} leads`);
  };

  // Selection-level export = only the selected leads.
  const exportSelected = () => {
    const rows = leads.filter((l) => selectedLeads.includes(l.id));
    if (rows.length === 0) return;
    const csv = buildCsv(rows);
    const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    downloadCsv(`outreachos-leads-selected-${ts}.csv`, csv);
    toast.success(`Exported ${rows.length} selected leads`);
  };

  const createList = async (name) => {
    try {
      const r = await api.post("/lists", { name });
      const created = r.data;
      setLists((prev) => [created, ...prev]);
      toast.success(`List "${name}" created`);
      return created;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatErr(detail, err.message || "Could not create list."));
      return null;
    }
  };

  const addSelectedToList = async (list_id) => {
    try {
      const r = await api.post("/leads/bulk-action", {
        action: "add_to_list",
        lead_ids: selectedLeads,
        list_id,
      });
      toast.success(`Added ${r.data?.modified ?? 0} lead(s) to list`);
      setAddToListOpen(false);
      // Refresh both lists (for counts) and leads (for list_ids).
      loadLists();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatErr(detail, err.message || "Bulk add failed."));
    }
  };

  const pushSelectedToCampaign = async (campaign_id) => {
    try {
      const r = await api.post("/leads/bulk-action", {
        action: "push_to_campaign",
        lead_ids: selectedLeads,
        campaign_id,
      });
      const inserted = r.data?.inserted ?? 0;
      const skipped = r.data?.skipped ?? 0;
      toast.success(
        skipped > 0
          ? `Pushed ${inserted} · skipped ${skipped} (duplicates)`
          : `Pushed ${inserted} lead(s) to campaign`,
      );
      setPushOpen(false);
      clearSelection();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatErr(detail, err.message || "Push to campaign failed."));
    }
  };

  const activeListName =
    lists.find((l) => l.id === activeListId)?.name || null;

  return (
    <div data-testid="leads-page" className="max-w-7xl">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-neutral-500 font-mono">
            <Users className="w-3.5 h-3.5" strokeWidth={1.8} />
            CRM
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-3 leading-tight">
            Leads
          </h1>
          <p className="mt-2 text-sm text-neutral-400 max-w-xl">
            Your contact book. Filter, tag, segment into lists — feed campaigns
            directly from here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="leads-refresh"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 rounded-md disabled:opacity-50"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              strokeWidth={1.8}
            />
            Refresh
          </button>
          <button
            onClick={handleExport}
            data-testid="leads-export-csv"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d] text-neutral-300 hover:text-white px-3 py-2 rounded-md"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.8} />
            Export CSV
          </button>
          <button
            onClick={() => setImportOpen(true)}
            data-testid="leads-import-csv"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d] text-neutral-300 hover:text-white px-3 py-2 rounded-md"
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={1.8} />
            Import CSV
          </button>
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="leads-new-button"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-4 py-2 rounded-md font-medium text-sm"
          >
            <Plus className="w-4 h-4" strokeWidth={1.8} />
            New lead
          </button>
        </div>
      </div>

      {/* Two-column layout: Lists sidebar + main table */}
      <div className="mt-7 grid grid-cols-12 gap-6">
        {/* Lists sidebar */}
        <aside className="col-span-12 md:col-span-3" data-testid="lists-sidebar">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              Lists
            </div>
            <button
              type="button"
              onClick={() => setNewListModalOpen(true)}
              data-testid="sidebar-new-list-button"
              className="p-1.5 text-neutral-500 hover:text-white hover:bg-[#161616] rounded-md transition-colors"
              aria-label="New list"
              title="New list"
            >
              <FolderPlus className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
          </div>
          <div className="rounded-lg border border-[#1a1a1a] bg-[#0a0a0a] overflow-hidden">
            <button
              type="button"
              onClick={() => setActiveListId("")}
              data-testid="list-filter-all"
              className={`w-full text-left px-3.5 py-2.5 flex items-center justify-between text-sm transition-colors ${
                !activeListId
                  ? "bg-[#101010] text-white"
                  : "text-neutral-400 hover:bg-[#0d0d0d] hover:text-white"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <InboxIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                All Leads
              </span>
              <span className="font-mono text-[11px] text-neutral-500">
                {leads.length}
              </span>
            </button>
            {lists.length === 0 ? (
              <div className="px-3.5 py-4 text-[11px] text-neutral-600 border-t border-[#161616]">
                No lists yet. Use the <span className="text-neutral-400">+</span>
                button above or "Add to list" after selecting leads.
              </div>
            ) : (
              lists.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setActiveListId(l.id)}
                  data-testid={`list-filter-${l.id}`}
                  className={`w-full text-left px-3.5 py-2.5 flex items-center justify-between text-sm border-t border-[#161616] transition-colors ${
                    activeListId === l.id
                      ? "bg-[#101010] text-white"
                      : "text-neutral-400 hover:bg-[#0d0d0d] hover:text-white"
                  }`}
                >
                  <span className="truncate">{l.name}</span>
                  <span className="font-mono text-[11px] text-neutral-500 ml-2 shrink-0">
                    {l.lead_count ?? 0}
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Main column */}
        <div className="col-span-12 md:col-span-9">
          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search
                className="w-3.5 h-3.5 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2"
                strokeWidth={1.8}
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="leads-search"
                placeholder="Search name, email, or company"
                className="w-full bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <TagIcon className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.8} />
              <select
                value={activeTag}
                onChange={(e) => setActiveTag(e.target.value)}
                data-testid="leads-tag-filter"
                className="bg-[#0d0d0d] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-3 py-2.5 text-sm text-white outline-none font-mono min-w-[160px]"
              >
                <option value="">All tags</option>
                {allTags.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 ml-auto"
              data-testid="leads-count"
            >
              {filtered.length}/{leads.length} shown
              {activeListName && (
                <span className="ml-2 text-neutral-300">· {activeListName}</span>
              )}
            </div>
          </div>

          {/* Action Bar — renders only when at least one lead is selected. */}
          {selectedLeads.length > 0 && (
            <div
              data-testid="bulk-action-bar"
              className="mt-4 flex items-center gap-3 flex-wrap rounded-lg border border-white/15 bg-white/[0.04] px-4 py-3"
            >
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white">
                {selectedLeads.length} selected
              </div>
              <div className="h-4 w-px bg-white/10" />
              <button
                type="button"
                onClick={exportSelected}
                data-testid="bulk-export-csv"
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d] text-neutral-300 hover:text-white px-3 py-2 rounded-md"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={1.8} />
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => setAddToListOpen(true)}
                data-testid="bulk-add-to-list"
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0d0d0d] text-neutral-300 hover:text-white px-3 py-2 rounded-md"
              >
                <ListPlus className="w-3.5 h-3.5" strokeWidth={1.8} />
                Add to list
              </button>
              <button
                type="button"
                onClick={() => setPushOpen(true)}
                data-testid="bulk-push-to-campaign"
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] bg-white text-black hover:bg-neutral-200 px-3 py-2 rounded-md"
              >
                <Send className="w-3.5 h-3.5" strokeWidth={1.8} />
                Push to campaign
              </button>
              <button
                type="button"
                onClick={clearSelection}
                data-testid="bulk-clear-selection"
                className="ml-auto text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white px-2 py-1"
              >
                Clear
              </button>
            </div>
          )}

          {/* Table */}
          <div className="mt-5 rounded-lg border border-[#1a1a1a] bg-[#0a0a0a] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-[#0d0d0d]">
                  <tr className="text-left text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                    <th className="px-4 py-3 font-normal w-[40px]">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someFilteredSelected;
                        }}
                        onChange={toggleSelectAll}
                        disabled={filtered.length === 0}
                        data-testid="leads-select-all"
                        aria-label="Select all leads"
                        className="cursor-pointer accent-white"
                      />
                    </th>
                    <th className="px-5 py-3 font-normal">Name</th>
                    <th className="px-5 py-3 font-normal">Email</th>
                    <th className="px-5 py-3 font-normal">Company</th>
                    <th className="px-5 py-3 font-normal">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && leads.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-16 text-center text-neutral-500">
                        <Loader2
                          className="w-4 h-4 animate-spin inline-block mr-2"
                          strokeWidth={1.8}
                        />
                        <span className="font-mono text-xs uppercase tracking-[0.2em]">
                          Loading leads…
                        </span>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-16 text-center">
                        <div className="text-neutral-500 text-sm">
                          {leads.length === 0
                            ? "No leads yet. Click \u201CNew lead\u201D to add your first contact."
                            : "No leads match these filters."}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((l) => {
                      const fullName =
                        `${l.first_name || ""} ${l.last_name || ""}`.trim() || "—";
                      const isSelected = selectedLeads.includes(l.id);
                      return (
                        <tr
                          key={l.id}
                          data-testid={`lead-row-${l.id}`}
                          className={`border-t border-[#161616] transition-colors ${
                            isSelected ? "bg-white/[0.03]" : "hover:bg-[#101010]"
                          }`}
                        >
                          <td className="px-4 py-3.5">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectOne(l.id)}
                              data-testid={`lead-select-${l.id}`}
                              aria-label={`Select ${l.email}`}
                              className="cursor-pointer accent-white"
                            />
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-md bg-[#161616] border border-[#1f1f1f] flex items-center justify-center text-[11px] font-mono text-neutral-400">
                                {initials(l.first_name, l.last_name, l.email)}
                              </div>
                              <div className="min-w-0">
                                <div className="text-white truncate">{fullName}</div>
                                <div className="text-[11px] font-mono text-neutral-600 truncate">
                                  {l.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5 text-neutral-300">
                              <Mail
                                className="w-3.5 h-3.5 text-neutral-600"
                                strokeWidth={1.8}
                              />
                              <span className="truncate">{l.email}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-neutral-300">
                            {l.company ? (
                              <div className="flex items-center gap-1.5">
                                <Building2
                                  className="w-3.5 h-3.5 text-neutral-600"
                                  strokeWidth={1.8}
                                />
                                <span className="truncate">{l.company}</span>
                              </div>
                            ) : (
                              <span className="text-neutral-700">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            {l.tags && l.tags.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {l.tags.map((t) => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() =>
                                      setActiveTag(activeTag === t ? "" : t)
                                    }
                                    className={`text-[10.5px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border transition-colors ${
                                      activeTag === t
                                        ? "bg-white/10 border-white/30 text-white"
                                        : "bg-[#101010] border-[#1f1f1f] text-neutral-400 hover:border-[#2a2a2a] hover:text-white"
                                    }`}
                                  >
                                    {t}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-neutral-700">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <NewLeadDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={handleCreated}
      />

      <ImportCsvModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={handleBulkImported}
      />

      <AddToListModal
        open={addToListOpen}
        onClose={() => setAddToListOpen(false)}
        lists={lists}
        onConfirm={addSelectedToList}
        onCreateList={createList}
        count={selectedLeads.length}
      />

      <PushToCampaignModal
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        onConfirm={pushSelectedToCampaign}
        count={selectedLeads.length}
      />

      <NewListModal
        open={newListModalOpen}
        onClose={() => setNewListModalOpen(false)}
        onCreate={createList}
      />
    </div>
  );
}
